/**
 * Banning is irreversible, so the modal spells that out and insists on a
 * reason (it becomes the partner's `ban` note). The modal owns the draft
 * reason; `onConfirm` resolves true once the ban is recorded, which
 * closes and clears it.
 */

import { useState } from 'react';

import { Alert, Button, Group, Modal, Stack, Textarea } from '@mantine/core';

interface PartnerBanModalProps {
  opened: boolean;
  partnerName: string | null | undefined;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<boolean>;
}

export function PartnerBanModal({
  opened,
  partnerName,
  onClose,
  onConfirm,
}: Readonly<PartnerBanModalProps>) {
  const [reason, setReason] = useState('');
  const [banning, setBanning] = useState(false);

  const close = () => {
    onClose();
    setReason('');
  };

  const confirm = async () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      return;
    }
    setBanning(true);
    try {
      if (await onConfirm(trimmed)) {
        close();
      }
    } finally {
      setBanning(false);
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Ban partner">
      <Stack gap="sm">
        <Alert color="red" title="This cannot be undone">
          Banning <strong>{partnerName ?? 'this partner'}</strong> freezes their profile for good:
          no more shortlisting, locking or edits, and there is no unban. Existing conversations and
          dispatched work are unaffected.
        </Alert>
        <Textarea
          label="Reason"
          description="Required — recorded as a note on the partner"
          required
          autosize
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          data-testid="np-ban-reason"
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={close}>
            Cancel
          </Button>
          <Button
            color="red"
            loading={banning}
            disabled={!reason.trim()}
            onClick={() => void confirm()}
            data-testid="np-ban-confirm"
          >
            Ban partner
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
